if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');

        if (!this.elements.viewer) return;

        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce(this.onSlideChanged.bind(this), 500)
        );

        if (this.elements.thumbnails) {
          this.elements.thumbnails
            .querySelectorAll('[data-target]')
            .forEach((thumbnail) => {
              thumbnail.querySelector('button')?.addEventListener(
                'click',
                this.setActiveMedia.bind(
                  this,
                  thumbnail.dataset.target,
                  false
                )
              );
            });
        }
      }

      connectedCallback() {
        this.initialFrame = requestAnimationFrame(() => {
          if (!this.isConnected || !this.elements.viewer) return;

          const generalMedia = this.elements.viewer.querySelector(
            '[data-media-id][data-variant-media="false"]'
          );

          if (generalMedia) {
            this.filterVariantMedia(null);

            this.elements.viewer
              .querySelectorAll('[data-media-id]')
              .forEach((item) => {
                item.classList.toggle('is-active', item === generalMedia);
              });

            generalMedia.parentElement.prepend(generalMedia);
            this.refreshGallery();

            generalMedia.parentElement.scrollTo({
              left: generalMedia.offsetLeft,
            });

            this.setActiveThumbnail(
              this.elements.thumbnails?.querySelector(
                `[data-target="${generalMedia.dataset.mediaId}"]`
              )
            );
          }

          if (
            this.dataset.desktopLayout?.includes('thumbnail') &&
            this.mql.matches
          ) {
            this.removeListSemantic();
          }
        });
      }

      disconnectedCallback() {
        cancelAnimationFrame(this.initialFrame);
      }

      filterVariantMedia(selectedMediaId) {
        const viewer = this.elements.viewer;
        if (!viewer) return;

        const hasGeneralMedia = !!viewer.querySelector(
          '[data-media-id][data-variant-media="false"]'
        );

        if (!hasGeneralMedia) return;

        viewer.querySelectorAll('[data-media-id]').forEach((item) => {
          const hide =
            item.dataset.variantMedia === 'true' &&
            item.dataset.mediaId !== selectedMediaId;

          if (hide) {
            item.style.setProperty('display', 'none', 'important');
          } else {
            item.style.removeProperty('display');
          }

          const thumbnail = this.elements.thumbnails?.querySelector(
            `[data-target="${item.dataset.mediaId}"]`
          );

          if (thumbnail) {
            if (hide) {
              thumbnail.style.setProperty('display', 'none', 'important');
            } else {
              thumbnail.style.removeProperty('display');
            }
          }
        });
      }

      refreshGallery() {
        this.elements.viewer?.resetPages?.();
        this.elements.thumbnails?.resetPages?.();
      }

      onSlideChanged(event) {
        const mediaId = event.detail?.currentElement?.dataset.mediaId;
        if (!mediaId) return;

        this.setActiveThumbnail(
          this.elements.thumbnails?.querySelector(
            `[data-target="${mediaId}"]`
          )
        );
      }

      setActiveMedia(mediaId, prepend) {
        const viewer = this.elements.viewer;
        if (!viewer) return;

        const activeMedia =
          viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          viewer.querySelector('[data-media-id]');

        if (!activeMedia) return;

        // The product's variant-update code passes prepend = true.
        if (prepend) {
          this.filterVariantMedia(activeMedia.dataset.mediaId);
        }

        viewer.querySelectorAll('[data-media-id]').forEach((item) => {
          item.classList.remove('is-active');
        });

        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);

          const thumbnail = this.elements.thumbnails?.querySelector(
            `[data-target="${activeMedia.dataset.mediaId}"]`
          );

          if (thumbnail) {
            thumbnail.parentElement.prepend(thumbnail);
          }
        }

        this.refreshGallery();
        this.preventStickyHeader();

        window.setTimeout(() => {
          if (!this.isConnected) return;

          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
            });
          }

          const rect = activeMedia.getBoundingClientRect();

          if (rect.top <= -0.5) {
            window.scrollTo({
              top: rect.top + window.scrollY,
              behavior: 'smooth',
            });
          }
        });

        this.playActiveMedia(activeMedia);

        const activeThumbnail = this.elements.thumbnails?.querySelector(
          `[data-target="${activeMedia.dataset.mediaId}"]`
        );

        this.setActiveThumbnail(activeThumbnail);

        if (activeThumbnail) {
          this.announceLiveRegion(
            activeMedia,
            activeThumbnail.dataset.mediaPosition
          );
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails.querySelectorAll('button').forEach(
          (button) => button.removeAttribute('aria-current')
        );

        thumbnail.querySelector('button')?.setAttribute(
          'aria-current',
          'true'
        );

        if (
          this.elements.thumbnails.isSlideVisible?.(thumbnail, 10)
        ) return;

        this.elements.thumbnails.slider?.scrollTo({
          left: thumbnail.offsetLeft,
        });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector(
          '.product__modal-opener--image img'
        );

        if (!image || !this.elements.liveRegion) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', 'false');
          this.elements.liveRegion.textContent =
            window.accessibilityStrings.imageAvailable.replace(
              '[index]',
              position
            );

          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', 'true');
          }, 2000);
        };

        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        activeItem.querySelector('.deferred-media')?.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader || document.querySelector('sticky-header');

        this.stickyHeader?.dispatchEvent(
          new Event('preventHeaderReveal')
        );
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;

        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems?.forEach((slide) => {
          slide.setAttribute('role', 'presentation');
        });
      }
    }
  );
}
